/**
 * PeerComparisonWidget - Compare company metrics against industry and peers
 *
 * Supports:
 * - Multiple metrics (PE, PB, ROE, ROA, etc.)
 * - Bar chart comparing: Ticker vs Industry vs Peers vs Individual peers
 * - Grid layout for multiple metrics
 * - Interactive tooltips
 */

import { useState, useMemo, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getChartTheme, CHART_COLORS, detectDarkMode, observeDarkMode, WIDGET_BACKGROUND } from './chartTheme';
import { useContainerWidth } from './hooks/useContainerWidth';

// Metric display names (Vietnamese) - matching Simplize UI
// Keys match API response structure (camelCase)
const METRIC_NAMES = {
    // Valuation (pePeer, pbPeer, evEbitdaPeer)
    pePeer: 'Chỉ số P/E',
    pbPeer: 'Chỉ số P/B',
    evEbitdaPeer: 'Chỉ số EV/EBITDA',
    // Future Growth (grfFuture)
    revenueEstGrowth: 'Dự báo tăng trưởng doanh thu 3 năm',
    netIncomeEstGrowth: 'Dự báo tăng trưởng lợi nhuận 3 năm',
    // Profit Margin (pastNetRp)
    profitMarginLtm: 'Biên lợi nhuận ròng 12 tháng',
    profitMarginAvg5y: 'Biên lợi nhuận ròng bình quân 5 năm',
    // Net Income Growth (pastNetGrf)
    netIncomeLtmGrowth: 'Tăng trưởng lợi nhuận 12 tháng',
    netIncome5yGrowth: 'Tăng trưởng lợi nhuận bình quân 5 năm',
    // ROE/ROA (pastRoeaPeer)
    roe: 'Chỉ số ROE',
    roa: 'Chỉ số ROA',
    // Debt/Equity (finDebtPeer)
    debtToEquity: 'Tỷ lệ Nợ vay/Vốn chủ sở hữu',
    // Banking metrics (pastNimPeer, finAqpa)
    niiLtm: 'Biên lãi ròng NIM (ngân hàng)',
    niiGrowth: 'Tăng trưởng NIM (ngân hàng)',
    nplRatioLtm: 'Tỷ lệ Nợ xấu/Cho vay (ngân hàng)',
    plRatioLtm: 'Tỷ lệ Dự phòng/Cho vay (ngân hàng)',
};

// Short names for chart titles
const METRIC_CHART_NAMES = {
    pePeer: 'P/E',
    pbPeer: 'P/B',
    evEbitdaPeer: 'EV/EBITDA',
    revenueEstGrowth: 'Dự báo tăng trưởng doanh thu',
    netIncomeEstGrowth: 'Dự báo tăng trưởng lợi nhuận',
    profitMarginLtm: 'Biên LN ròng 12T',
    profitMarginAvg5y: 'Biên LN ròng TB 5 năm',
    netIncomeLtmGrowth: 'Tăng trưởng LN 12T',
    netIncome5yGrowth: 'Tăng trưởng LN TB 5 năm',
    roe: 'ROE',
    roa: 'ROA',
    debtToEquity: 'Nợ vay/VCSH',
    niiLtm: 'NIM',
    niiGrowth: 'Tăng trưởng NIM',
    nplRatioLtm: 'Tỷ lệ nợ xấu',
    plRatioLtm: 'Tỷ lệ dự phòng',
};

// Banking-only metrics (enabled when industryGroupCode === 'BANK')
const BANKING_METRICS = ['niiLtm', 'niiGrowth', 'nplRatioLtm', 'plRatioLtm'];

// Map from API request param names to widget metric keys
// API params: PE, PB, EV_EBITDA, GRF_FUTURE, PAST_NET_RP, PAST_NET_GRF, PAST_ROAE_PEER, FIN_DEBT_PEER, PAST_NIM_PEER, FIN_AQPA
const API_PARAM_TO_METRICS = {
    'PE': ['pePeer'],
    'PB': ['pbPeer'],
    'EV_EBITDA': ['evEbitdaPeer'],
    'GRF_FUTURE': ['revenueEstGrowth', 'netIncomeEstGrowth'],
    'PAST_NET_RP': ['profitMarginLtm', 'profitMarginAvg5y'],
    'PAST_NET_GRF': ['netIncomeLtmGrowth', 'netIncome5yGrowth'],
    'PAST_ROAE_PEER': ['roe', 'roa'],
    'FIN_DEBT_PEER': ['debtToEquity'],
    'PAST_NIM_PEER': ['niiLtm', 'niiGrowth'],
    'FIN_AQPA': ['nplRatioLtm', 'plRatioLtm'],
};

// Convert API params to widget metric keys
function apiParamsToMetricKeys(apiParams) {
    if (!apiParams || !Array.isArray(apiParams)) return [];
    const metrics = [];
    apiParams.forEach(param => {
        const mapped = API_PARAM_TO_METRICS[param];
        if (mapped) {
            metrics.push(...mapped);
        }
    });
    return metrics;
}

// Map metric keys to API response data extraction
// Keys match the valueKey in API response for consistency
const METRIC_DATA_MAPPING = {
    // Valuation ratios (pePeer, pbPeer, evEbitdaPeer)
    pePeer: { dataKey: 'pePeer', valueKey: 'ratio', industryKey: 'ratioIndustry', peerKey: 'ratioPeer', itemsKey: 'items', format: 'ratio' },
    pbPeer: { dataKey: 'pbPeer', valueKey: 'ratio', industryKey: 'ratioIndustry', peerKey: 'ratioPeer', itemsKey: 'items', format: 'ratio' },
    evEbitdaPeer: { dataKey: 'evEbitdaPeer', valueKey: 'ratio', industryKey: 'ratioIndustry', peerKey: 'ratioPeer', itemsKey: 'items', format: 'ratio' },
    // Future growth estimates (grfFuture)
    revenueEstGrowth: { dataKey: 'grfFuture', valueKey: 'revenueEstGrowth', industryKey: 'revenueEstGrowthIndustry', peerKey: 'revenueEstGrowthPeer', itemsKey: 'items', valueField: 'revenueEstGrowth', format: 'percent' },
    netIncomeEstGrowth: { dataKey: 'grfFuture', valueKey: 'netIncomeEstGrowth', industryKey: 'netIncomeEstGrowthIndustry', peerKey: 'netIncomeEstGrowthPeer', itemsKey: 'items', valueField: 'netIncomeEstGrowth', format: 'percent' },
    // Profit margin (pastNetRp)
    profitMarginLtm: { dataKey: 'pastNetRp', valueKey: 'profitMarginLtm', industryKey: 'profitMarginIndustryLtm', peerKey: 'profitMarginPeer', itemsKey: 'items', valueField: 'profitMarginLtm', format: 'percent' },
    profitMarginAvg5y: { dataKey: 'pastNetRp', valueKey: 'profitMarginAvg5y', industryKey: 'profitMarginIndustryAvg5y', peerKey: 'profitMarginAvg5yPeer', itemsKey: 'items', valueField: 'profitMarginAvg5y', format: 'percent' },
    // Net income growth (pastNetGrf)
    netIncomeLtmGrowth: { dataKey: 'pastNetGrf', valueKey: 'netIncomeLtmGrowth', industryKey: 'netIncomeLtmGrowthIndustry', peerKey: 'netIncomeLtmGrowthPeer', itemsKey: 'items', valueField: 'netIncomeLtmGrowth', format: 'percent' },
    netIncome5yGrowth: { dataKey: 'pastNetGrf', valueKey: 'netIncome5yGrowth', industryKey: 'netIncome5yGrowthIndustry', peerKey: 'netIncome5yGrowthPeer', itemsKey: 'items', valueField: 'netIncome5yGrowth', format: 'percent' },
    // ROE/ROA (pastRoeaPeer)
    roe: { dataKey: 'pastRoeaPeer', valueKey: 'roe', industryKey: 'roeAvgIndustry', peerKey: 'roePeer', itemsKey: 'items', valueField: 'roe', format: 'percent' },
    roa: { dataKey: 'pastRoeaPeer', valueKey: 'roa', industryKey: 'roaAvgIndustry', peerKey: 'roaPeer', itemsKey: 'items', valueField: 'roa', format: 'percent' },
    // Debt/Equity (finDebtPeer)
    debtToEquity: { dataKey: 'finDebtPeer', valueKey: 'debtToEquity', industryKey: 'debtToEquityIndustry', peerKey: 'debtToEquityPeer', itemsKey: 'items', valueField: 'debtToEquity', format: 'percent' },
    // Banking: NIM (pastNimPeer)
    niiLtm: { dataKey: 'pastNimPeer', valueKey: 'niiLtm', industryKey: 'niiIndustry', peerKey: 'niiLtmPeer', itemsKey: 'items', valueField: 'niiLtm', format: 'percent' },
    niiGrowth: { dataKey: 'pastNimPeer', valueKey: 'niiGrowth', industryKey: 'niiGrowthIndustry', peerKey: 'niiGrowthPeer', itemsKey: 'items', valueField: 'niiGrowth', format: 'percent' },
    // Banking: Asset quality (finAqpa)
    nplRatioLtm: { dataKey: 'finAqpa', valueKey: 'nplRatioLtm', industryKey: 'nplRatioIndustry', peerKey: 'nplRatioLtmPeer', itemsKey: 'items', valueField: 'nplRatioLtm', format: 'percent' },
    plRatioLtm: { dataKey: 'finAqpa', valueKey: 'plRatioLtm', industryKey: 'plRatioIndustry', peerKey: 'plRatioLtmPeer', itemsKey: 'items', valueField: 'plRatioLtm', format: 'percent' },
};

// Format value based on type
function formatValue(value, format = 'number') {
    if (value === null || value === undefined) return '-';

    switch (format) {
        case 'percent':
            return `${value.toFixed(2)}%`;
        case 'ratio':
            return `${value.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}x`;
        default:
            return value.toLocaleString('vi-VN', { maximumFractionDigits: 2 });
    }
}

// Extract metric data from peer comparison response
function extractMetricData(data, ticker, metricKey) {
    const mapping = METRIC_DATA_MAPPING[metricKey];
    if (!mapping) return null;

    const metricData = data[mapping.dataKey];
    if (!metricData) return null;

    let tickerValue = metricData[mapping.valueKey];
    const industryValue = metricData[mapping.industryKey];
    const peerAvgValue = metricData[mapping.peerKey];
    let peers = [];

    // Extract individual peer data
    if (metricData[mapping.itemsKey] && Array.isArray(metricData[mapping.itemsKey])) {
        const valueField = mapping.valueField || 'value';
        const allItems = metricData[mapping.itemsKey]
            .filter(item => item && item.ticker)
            .map(item => ({
                ticker: item.ticker,
                value: item[valueField],
            }))
            .filter(item => item.value !== null && item.value !== undefined);

        // Find main ticker's value from items if not available from main field
        if (tickerValue === null || tickerValue === undefined) {
            const selfItem = allItems.find(item => item.ticker === ticker);
            if (selfItem) {
                tickerValue = selfItem.value;
            }
        }

        // Filter out main ticker from peers list
        peers = allItems.filter(item => item.ticker !== ticker);
    }

    const result = {
        ticker,
        tickerValue,
        industryValue,
        peerAvgValue,
        peers,
        format: mapping.format,
    };

    // Check if we have at least one value to display
    const hasAnyValue =
        tickerValue !== null && tickerValue !== undefined ||
        industryValue !== null && industryValue !== undefined ||
        peerAvgValue !== null && peerAvgValue !== undefined ||
        peers.length > 0;

    if (!hasAnyValue) return null;

    return result;
}

// Check if a metric has actual data in the response
function hasMetricData(data, metricKey) {
    const mapping = METRIC_DATA_MAPPING[metricKey];
    if (!mapping) return false;

    const metricData = data[mapping.dataKey];
    if (!metricData) return false;

    // Check if any of the values exist
    const hasTickerValue = metricData[mapping.valueKey] !== null && metricData[mapping.valueKey] !== undefined;
    const hasIndustryValue = metricData[mapping.industryKey] !== null && metricData[mapping.industryKey] !== undefined;
    const hasPeerValue = metricData[mapping.peerKey] !== null && metricData[mapping.peerKey] !== undefined;
    const hasItems = metricData[mapping.itemsKey] && Array.isArray(metricData[mapping.itemsKey]) && metricData[mapping.itemsKey].length > 0;

    return hasTickerValue || hasIndustryValue || hasPeerValue || hasItems;
}

// Get available metrics based on industry type AND actual data existence
// Banking metrics only available when industryGroupCode === 'BANK'
function getAvailableMetrics(data) {
    if (!data) return [];

    const isBank = data.industryGroupCode === 'BANK';
    const allMetrics = Object.keys(METRIC_DATA_MAPPING);

    // Filter: check banking restriction AND check if data actually exists
    return allMetrics.filter(key => {
        // Banking metrics only for banks
        if (BANKING_METRICS.includes(key) && !isBank) {
            return false;
        }
        // Check if metric has actual data
        return hasMetricData(data, key);
    });
}

// Group metrics by category for better UI organization
// Keys match API response structure (camelCase)
const METRIC_CATEGORIES = {
    'Định giá': ['pePeer', 'pbPeer', 'evEbitdaPeer'],
    'Dự báo tăng trưởng': ['revenueEstGrowth', 'netIncomeEstGrowth'],
    'Biên lợi nhuận': ['profitMarginLtm', 'profitMarginAvg5y'],
    'Tăng trưởng lợi nhuận': ['netIncomeLtmGrowth', 'netIncome5yGrowth'],
    'Hiệu quả sinh lời': ['roe', 'roa'],
    'Cấu trúc tài chính': ['debtToEquity'],
    'Ngân hàng': ['niiLtm', 'niiGrowth', 'nplRatioLtm', 'plRatioLtm'],
};

// Chart colors matching Simplize design
const PEER_CHART_COLORS = {
    ticker: '#F59E0B',      // Amber/Orange for main ticker
    industry: '#3B82F6',    // Blue for industry average
    peers: '#60A5FA',       // Lighter blue for peers average
    peerItems: '#BFDBFE',   // Very light blue for individual peers
    referenceLine: '#F59E0B', // Orange dashed line
};

// Build chart options for a single metric
function buildChartOptions(metricData, metricKey, isDarkMode) {
    if (!metricData) return null;

    const theme = getChartTheme(isDarkMode);
    const metricName = METRIC_CHART_NAMES[metricKey] || METRIC_NAMES[metricKey] || metricKey;

    // Build categories and data
    const categories = [];
    const values = [];
    let tickerValue = null;
    let aggregateCount = 0; // Count of aggregate columns (ticker, industry, peers avg)

    // 1. Ticker value (orange)
    if (metricData.tickerValue !== null && metricData.tickerValue !== undefined) {
        tickerValue = metricData.tickerValue;
        categories.push(metricData.ticker);
        values.push({
            y: metricData.tickerValue,
            color: PEER_CHART_COLORS.ticker,
        });
        aggregateCount++;
    }

    // 2. Industry average (blue)
    if (metricData.industryValue !== null && metricData.industryValue !== undefined) {
        categories.push('Ngành');
        values.push({
            y: metricData.industryValue,
            color: PEER_CHART_COLORS.industry,
        });
        aggregateCount++;
    }

    // 3. Peer average (lighter blue)
    if (metricData.peerAvgValue !== null && metricData.peerAvgValue !== undefined) {
        categories.push('TB Peers');
        values.push({
            y: metricData.peerAvgValue,
            color: PEER_CHART_COLORS.peers,
        });
        aggregateCount++;
    }

    // 4. Individual peers (very light blue) - show all peers
    metricData.peers.forEach((peer) => {
        categories.push(peer.ticker);
        values.push({
            y: peer.value,
            color: PEER_CHART_COLORS.peerItems,
        });
    });

    // If no data to display, return null
    if (values.length === 0) return null;

    const textColor = isDarkMode ? 'rgba(229, 229, 229, 0.9)' : 'rgba(51, 51, 51, 0.9)';
    const mutedColor = isDarkMode ? 'rgba(229, 229, 229, 0.6)' : 'rgba(51, 51, 51, 0.6)';
    const gridColor = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';

    // PlotBands: Highlight aggregate columns (ticker, industry, peers avg) with background
    // PlotLines: Vertical separator line
    const xAxisPlotBands = [];
    const xAxisPlotLines = [];

    if (aggregateCount > 0 && metricData.peers.length > 0) {
        // Background band for aggregate columns (first 3) - no label, use visual separation only
        xAxisPlotBands.push({
            from: -0.5,
            to: aggregateCount - 0.5,
            color: isDarkMode ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.06)', // Light blue tint
        });

        // Vertical separator line
        xAxisPlotLines.push({
            value: aggregateCount - 0.5,
            color: isDarkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)',
            width: 1,
            dashStyle: 'Dash',
            zIndex: 5,
        });
    }

    return {
        chart: {
            ...theme.chart,
            type: 'column',
            height: 220,
            marginBottom: 45,
        },
        title: {
            text: null,
        },
        credits: theme.credits,
        legend: { enabled: false },
        xAxis: {
            ...theme.xAxis,
            categories,
            lineWidth: 0, // No axis line
            tickLength: 0, // No tick marks
            gridLineWidth: 0, // No vertical grid lines
            plotBands: xAxisPlotBands,
            plotLines: xAxisPlotLines,
            labels: {
                ...theme.xAxis.labels,
                rotation: -45,
                style: {
                    fontSize: '10px',
                    color: mutedColor,
                },
                step: 1,
                allowOverlap: false,
            },
        },
        yAxis: {
            ...theme.yAxis,
            title: { text: null },
            gridLineWidth: 0, // No grid lines
            labels: {
                ...theme.yAxis.labels,
                formatter: function() {
                    return formatValue(this.value, metricData.format);
                },
            },
            // Reference line from ticker value
            plotLines: tickerValue !== null ? [{
                value: tickerValue,
                color: PEER_CHART_COLORS.referenceLine,
                dashStyle: 'Dash',
                width: 1.5,
                zIndex: 3,
                label: {
                    text: formatValue(tickerValue, metricData.format),
                    align: 'right',
                    x: -5,
                    style: {
                        color: PEER_CHART_COLORS.referenceLine,
                        fontWeight: '600',
                        fontSize: '11px',
                    }
                }
            }] : [],
        },
        tooltip: {
            ...theme.tooltip,
            useHTML: true,
            formatter: function() {
                // Get category name from xAxis categories array using point index
                const categoryName = categories[this.point.index] || this.key || this.x || '';
                return `
                    <div style="padding: 4px 2px">
                        <div style="font-weight: 600; margin-bottom: 6px; color: ${textColor}">${categoryName}</div>
                        <div style="display: flex; align-items: center; gap: 8px; font-size: 12px">
                            <span style="color:${this.point.color}">●</span>
                            <span style="color: ${mutedColor}">${metricName}:</span>
                            <span style="font-weight: 600; color: ${textColor}">${formatValue(this.y, metricData.format)}</span>
                        </div>
                    </div>
                `;
            },
        },
        plotOptions: {
            column: {
                ...theme.plotOptions.column,
                colorByPoint: true,
                borderRadius: 2,
                pointPadding: 0.1,
                groupPadding: 0.15,
                dataLabels: {
                    enabled: false,
                },
            },
        },
        series: [{
            name: metricName,
            data: values,
        }],
    };
}

/**
 * Main Peer Comparison Widget
 */
export default function PeerComparisonWidget({ data: initialData, config }) {
    const [isDarkMode, setIsDarkMode] = useState(detectDarkMode);
    const [selectedMetrics, setSelectedMetrics] = useState([]);
    const [showSettings, setShowSettings] = useState(false);

    // Use reusable hook for responsive container width detection
    const { containerRef, containerWidth, isMobile } = useContainerWidth(500);

    // Observe dark mode changes
    useEffect(() => {
        return observeDarkMode(setIsDarkMode);
    }, []);

    // Extract config
    const { params = {} } = config || {};
    const rawTickers = params.tickers || [];
    const tickers = Array.isArray(rawTickers) ? rawTickers : (typeof rawTickers === 'string' ? [rawTickers] : []);
    const { metrics: configMetrics = [] } = params;
    const ticker = tickers[0] || '';

    // Get data for the ticker (handle various response structures)
    const tickerData = useMemo(() => {
        if (!initialData) return null;

        // Multi-ticker response: { HPG: {...}, VNM: {...} }
        if (ticker && initialData[ticker]) {
            return initialData[ticker];
        }

        // Single ticker response: { industryGroupCode, pePeer, ... }
        if (initialData.industryGroupCode !== undefined) {
            return initialData;
        }

        // Response might be nested in 'data' field
        if (initialData.data) {
            if (ticker && initialData.data[ticker]) {
                return initialData.data[ticker];
            }
            if (initialData.data.industryGroupCode !== undefined) {
                return initialData.data;
            }
        }

        // Try to find ticker data in any nested object
        const keys = Object.keys(initialData);
        for (const key of keys) {
            const obj = initialData[key];
            if (obj && typeof obj === 'object' && obj.industryGroupCode !== undefined) {
                return obj;
            }
        }

        return null;
    }, [initialData, ticker]);

    // Get available metrics
    const availableMetrics = useMemo(() => getAvailableMetrics(tickerData), [tickerData]);

    // Initialize selected metrics from config or defaults (support multiple metrics)
    useEffect(() => {
        if (selectedMetrics.length === 0 && availableMetrics.length > 0) {
            // Convert API params to widget metric keys
            const mappedMetrics = apiParamsToMetricKeys(configMetrics);

            // Filter to only available metrics
            const validMetrics = mappedMetrics.filter(m => availableMetrics.includes(m));

            if (validMetrics.length > 0) {
                // Use up to 4 metrics from config
                setSelectedMetrics(validMetrics.slice(0, 4));
            } else {
                // Default: first 4 available metrics
                setSelectedMetrics(availableMetrics.slice(0, 4));
            }
        }
    }, [availableMetrics, configMetrics, selectedMetrics.length]);

    // Toggle metric selection (multi-select)
    const toggleMetric = (metricKey) => {
        setSelectedMetrics(prev => {
            if (prev.includes(metricKey)) {
                // Don't allow deselecting if it's the last one
                if (prev.length === 1) return prev;
                return prev.filter(m => m !== metricKey);
            } else {
                // Add metric (no limit)
                return [...prev, metricKey];
            }
        });
    };

    // Select all metrics
    const selectAllMetrics = () => {
        setSelectedMetrics(availableMetrics);
    };

    // Clear to single metric
    const clearToSingle = () => {
        setSelectedMetrics([selectedMetrics[0] || availableMetrics[0]]);
    };


    // No data
    if (!tickerData || availableMetrics.length === 0) {
        return (
            <div className="border border-gray-100 dark:border-gray-800 rounded-xl p-4 text-center text-gray-500" style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
                Không có dữ liệu so sánh peer
            </div>
        );
    }

    // Determine grid columns based on selected metrics count
    const getGridCols = (count) => {
        if (count === 1) return 'grid-cols-1';
        if (count === 2) return 'grid-cols-1 md:grid-cols-2';
        return 'grid-cols-1 md:grid-cols-2';
    };

    return (
        <div ref={containerRef} className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden" style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
            {/* Header with dropdown */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            So sánh ngành - {ticker}
                        </span>
                        {selectedMetrics.length > 1 && (
                            <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full">
                                {selectedMetrics.length} chỉ số
                            </span>
                        )}
                    </div>
                    {/* Metric dropdown selector */}
                    <div className="relative">
                        <button
                            onClick={() => setShowSettings(!showSettings)}
                            className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            title="Chọn chỉ số"
                        >
                            {/* Bar chart icon - for selecting data series */}
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Metric Dropdown Panel - Multi-select */}
            {showSettings && (
                <div className="border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                    {/* Quick actions */}
                    <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                        <button
                            onClick={selectAllMetrics}
                            className="px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                        >
                            Chọn tất cả
                        </button>
                        <button
                            onClick={clearToSingle}
                            className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                            Chỉ giữ 1
                        </button>
                        <button
                            onClick={() => setShowSettings(false)}
                            className="ml-auto px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                            Đóng
                        </button>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                        {Object.entries(METRIC_CATEGORIES).map(([category, metrics]) => {
                            // Filter to only show categories with available metrics
                            const availableInCategory = metrics.filter(m => availableMetrics.includes(m));
                            if (availableInCategory.length === 0) return null;

                            return (
                                <div key={category}>
                                    <div className="px-4 py-2 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider bg-gray-50 dark:bg-gray-900/50 sticky top-0">
                                        {category}
                                    </div>
                                    {metrics.map(metricKey => {
                                        const isSelected = selectedMetrics.includes(metricKey);
                                        const isAvailable = availableMetrics.includes(metricKey);
                                        if (!isAvailable) return null; // Hide unavailable metrics

                                        return (
                                            <button
                                                key={metricKey}
                                                onClick={() => toggleMetric(metricKey)}
                                                className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                                                    isSelected
                                                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    {/* Checkbox */}
                                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                                                        isSelected
                                                            ? 'bg-blue-500 border-blue-500'
                                                            : 'border-gray-300 dark:border-gray-600'
                                                    }`}>
                                                        {isSelected && (
                                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span>{METRIC_NAMES[metricKey] || metricKey}</span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Charts Grid */}
            <div className={`grid ${getGridCols(selectedMetrics.length)} gap-3 py-2 px-3`}>
                {selectedMetrics.map(metricKey => {
                    const chartData = extractMetricData(tickerData, ticker, metricKey);
                    if (!chartData) return null;

                    return (
                        <div key={metricKey} className="relative">
                            {/* Metric title - centered */}
                            <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 text-center">
                                {METRIC_CHART_NAMES[metricKey] || METRIC_NAMES[metricKey] || metricKey}
                            </div>
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={buildChartOptions(chartData, metricKey, isDarkMode)}
                            />
                        </div>
                    );
                })}
            </div>

            {/* Legend */}
            <div className="px-4 pb-3 flex flex-wrap items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: PEER_CHART_COLORS.ticker }}></span>
                    <span className="text-gray-600 dark:text-gray-400">{ticker}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: PEER_CHART_COLORS.industry }}></span>
                    <span className="text-gray-600 dark:text-gray-400">Ngành</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: PEER_CHART_COLORS.peers }}></span>
                    <span className="text-gray-600 dark:text-gray-400">TB Peers</span>
                </div>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: PEER_CHART_COLORS.peerItems }}></span>
                    <span className="text-gray-600 dark:text-gray-400">Peers riêng lẻ</span>
                </div>
            </div>
        </div>
    );
}
